/**
 * Pure form state + validation for the employee create/edit form (#22335).
 *
 * Mirrors the backend's real input schema and cross-field validation exactly
 * -- `employeeProfileInput`/`validateEmployeeProfile`/`assertValidNicAndEpf`
 * (`apps/api/src/trpc/routers/payroll.ts`,
 * `apps/api/src/trpc/routers/payroll_employee_validation.ts`,
 * `apps/api/src/lib/sl_nic_epf_validation.ts`) -- so a bad value is caught
 * client-side before a round trip, without duplicating those modules via a
 * cross-app import (`apps/api` is a Workers backend, not a package this app
 * can depend on). Every check here has a byte-for-byte-matching backend
 * counterpart; if the backend rule changes, this file must change with it.
 *
 * No React here -- this is a pure `.ts` module per CLAUDE.md's Component
 * Testability rule (arithmetic/validation -> `*_validation.ts`).
 *
 * ── Update semantics: diff against the loaded record, never blank-to-null ──
 *
 * (Independent adversarial review finding, #22335: an earlier version of
 * this module built the SAME wire payload for create and update -- blank
 * field -> `null`. For CREATE that's correct (there is nothing to destroy).
 * For UPDATE it silently WIPED every field the form doesn't populate from a
 * fresh read on every save: `RealEmployeeRecord` didn't carry
 * `nic_number`/`epf_member_no`/`bank_code`/`npwp`/`nik`/`ptkp_status`/
 * `bpjs_jkk_risk_class` at all, so the edit form always showed them blank
 * and always sent `null` for them -- destroying real EPF/NIC/statutory data
 * on every unrelated edit, e.g. fixing a typo in a name. A second bug in the
 * same family: a hardcoded `preferred_locale: 'en'` was sent on every save,
 * silently overwriting a real si/ta/id payslip-locale preference.)
 *
 * The fix: {@link RealEmployeeRecord} now carries every field this form
 * edits (`wagepro_store_employees.ts`), {@link employeeRecordToFormDraft}
 * loads them into the draft instead of leaving them blank, and
 * {@link buildUpdateEmployeeInput} takes the ORIGINAL record as a second
 * argument and only includes a field in the wire payload when the draft's
 * value actually differs from it -- an explicit `undefined` for every
 * untouched field, which zod's `.optional()` (and the backend's own
 * `if (input.x !== undefined) updates.x = ...` per-field guard) treats
 * identically to an omitted key. `preferred_locale` has no UI control on
 * this form at all (out of scope for #22335) and is therefore never
 * included in either wire payload -- the backend's own `?? 'en'` create
 * default and untouched update value apply exactly as if this form did not
 * exist for that field.
 *
 * CREATE keeps the simpler blank-string -> `null` mapping
 * ({@link buildCreateEmployeeInput}) -- there is no prior record to diff
 * against, and every optional field genuinely starts unset.
 *
 * ── Jurisdiction gating ──────────────────────────────────────────────────────
 *
 * The backend's `employeeProfileInput` carries BOTH Sri Lanka fields
 * (`nic_number`/`epf_member_no`) and Indonesia fields
 * (`npwp`/`nik`/`ptkp_status`/`bpjs_jkk_risk_class`) unconditionally -- it has
 * no per-entity "jurisdiction" concept of its own beyond the entity's real
 * `country` column (`apps/api/src/db/schema/control_plane/entities.ts`,
 * default `'LK'`), which is not yet surfaced to this app's frontend by any
 * hook (`OrganisationSettings`/`RealOrganisationRecord`,
 * `wagepro_store_settings.ts`, deliberately dropped it -- out of scope for
 * that pass). Rather than inventing a fake signal, this module derives a
 * pragmatic jurisdiction proxy from the entity's real, already-fetched
 * `base_currency` (`useSettings()`) -- LKR implies Sri Lanka, IDR implies
 * Indonesia, matching how the backend itself gates its Indonesia-specific
 * PPh21/BPJS statutory engine on `country === 'ID'`
 * (`resolvePayrollEngineForEntity`, `approvePayRun`). This is documented,
 * not silent: {@link deriveJurisdiction}'s own doc comment repeats this
 * caveat, and a future pass that threads the real `entities.country` column
 * through `entity.clientGet` should replace this proxy outright rather than
 * layering onto it.
 *
 * `useSettings().organisation` falls back to `MOCK_ORGANISATION_SETTINGS`
 * (base_currency `'LKR'`) only in a window the real app never reaches --
 * `main.tsx`'s `AuthGate` mounts `WageProStoreProvider` with
 * `organisation_record={sessionFactsState.organisation}` only once the real
 * `entity.clientGet`-derived session facts have resolved (see
 * `mock_data.test.ts`'s `PRODUCTION_REACHABLE` entry for
 * `MOCK_ORGANISATION_SETTINGS`, which documents this exact invariant for the
 * settings screen this jurisdiction proxy also depends on). A test harness
 * that mounts these employee screens directly, bypassing `AuthGate`, is the
 * one place the mock LKR default could leak through -- acceptable there
 * (defaults to the SL fields, never crashes), not reachable in production.
 *
 * ── Deliberate scope gap: `manager` ────────────────────────────────────────
 *
 * The Job info read view (`profile_tabs/job_tab.tsx`) also displays
 * `manager` ("Reports to"), and the backend's `employeeProfileInput`
 * accepts it on create AND update exactly like
 * `job_title`/`department`/`work_location` above. This form still has no
 * control for it -- left open as a disclosed decision, not an oversight
 * (`manager` is a plain text field identical in shape to the three above).
 *
 * ── Worker type ──────────────────────────────────────────────────────────────
 *
 * `employment_type` (full-time / part-time / fixed-term / contractor) and,
 * for a part-timer only, `fte_percent` (whole percent 1-100, sent as
 * `fte_bps` = percent x 100). Mirrors the backend's
 * `validateWorkerTypeFields`: a non-part-time worker never sends an FTE
 * (`null` on create; on update `null` only when one is on file), so leaving
 * part-time clears it in the same call. On update the FTE is only sent when
 * the user edited the displayed percent, so a non-whole stored FTE is never
 * rewritten by an unrelated save. A contractor's hidden EPF / PTKP / BPJS
 * fields are never submitted (cleared when a value is on file). A contractor is never paid through a pay run, so the
 * salaried "positive salary" rule is skipped for them (the pay fields are
 * hidden on the form) -- mirroring `validateEmployeeProfile`.
 */
import { validateSlNic, validateSlEpfNumber } from '@jumpstone/core';
import {
  deriveApitEmploymentType,
  deriveEmploymentType,
  deriveResidencyStatus,
  type RealEmployeeRecord,
} from '../../data/wagepro_store_employees.js';
import type {
  ApitEmploymentType,
  EmploymentType,
  TaxResidency,
} from '../../data/types';
import { fte_bps_to_percent, fte_percent_to_bps } from '../../lib/worker_types';
import {
  buildCompensationHistoryInput,
  historyRowsHaveErrors,
  todayIsoUtc,
  type CompensationHistoryDraft,
  type CompensationHistoryInput,
} from './employee_form_compensation_utils.js';

/** Mirrors the backend's `PAY_BASIS` enum (`apps/api/src/trpc/routers/payroll.ts`) and `RealEmployeeRecord.pay_basis`. */
export type PayBasis = 'salaried' | 'hourly';

/** The two jurisdiction-specific ID field groups this form knows how to gate. Neither is shown for any other currency/jurisdiction. */
export type EmployeeJurisdiction = 'LK' | 'ID' | 'OTHER';

/**
 * Derives a pragmatic jurisdiction proxy from the entity's real base
 * currency (see module docblock for why this is a proxy, not the real
 * `entities.country` column). Comparison is case-insensitive and trims
 * surrounding whitespace so a currency code sourced from a free-text
 * control-plane column round-trips safely.
 */
export function deriveJurisdiction(
  base_currency: string,
): EmployeeJurisdiction {
  const normalised = base_currency.trim().toUpperCase();
  if (normalised === 'LKR') return 'LK';
  if (normalised === 'IDR') return 'ID';
  return 'OTHER';
}

/** The 12 PTKP (Indonesia dependant-status) codes the backend's `ptkp_status` enum accepts -- copied verbatim from `employeeProfileInput` (`apps/api/src/trpc/routers/payroll.ts`). */
export const PTKP_STATUS_OPTIONS = [
  'TK/0',
  'TK/1',
  'TK/2',
  'TK/3',
  'K/0',
  'K/1',
  'K/2',
  'K/3',
  'K/I/0',
  'K/I/1',
  'K/I/2',
  'K/I/3',
] as const;
export type PtkpStatus = (typeof PTKP_STATUS_OPTIONS)[number];

/** The BPJS JKK (Indonesia work-injury insurance) risk-class range the backend accepts -- `z.number().int().min(1).max(5)`. */
export const BPJS_JKK_RISK_CLASSES = [1, 2, 3, 4, 5] as const;

/**
 * The form's editable draft state. Money fields are always minor units
 * (cents), matching `<MoneyInput>`'s contract (docs/money-rules.md) -- never
 * a decimal string. Every optional backend field is represented as an empty
 * string / `null` rather than `undefined`, so every field is always a
 * controlled input. No `preferred_locale` field -- see module docblock.
 */
export interface EmployeeFormDraft {
  full_name: string;
  email: string;
  pay_basis: PayBasis;
  /**
   * ISO 4217 pay currency, or `''` for "the organisation base currency" --
   * the form's mirror of the backend's NULL (`payroll_pay_currency.ts`).
   * The picker writes `''` whenever the base is chosen, so a legacy NULL row
   * is never rewritten to an explicit base code.
   */
  pay_currency_code: string;
  monthly_salary_cents: number;
  hourly_rate_cents: number;
  employment_start_date: string;
  termination_date: string;
  is_active: boolean;
  /** Employer-assigned employee number (MIGRATION_V395); `''` = none. */
  employee_number: string;
  epf_member_no: string;
  nic_number: string;
  bank_name: string;
  bank_account_number: string;
  bank_branch: string;
  bank_code: string;
  npwp: string;
  nik: string;
  ptkp_status: PtkpStatus | '';
  bpjs_jkk_risk_class: number | null;
  job_title: string;
  department: string;
  work_location: string;
  employment_type: EmploymentType;
  /** SL tax residency (#26653); with `is_citizen` selects IRD Table 4. */
  residency_status: TaxResidency;
  is_citizen: boolean;
  /** SL APIT routing (ADR-168): `secondary` = IRD Table 07. Not the worker type above. */
  apit_employment_type: ApitEmploymentType;
  /** Declared monthly primary income in minor units; `null` = none declared (APIT 36%). Meaningful only when secondary. */
  apit_declared_primary_income_cents: number | null;
  /** The employer bears this employee's income tax (IRD Table 06, ADR-170). Ignored when {@link isApitBorneUnavailable}. */
  apit_borne_by_employer: boolean;
  /** Whole percent of full-time (1-100) as typed; meaningful only for `part_time`. */
  fte_percent: string;
  /**
   * CREATE-only: the date `pay_basis` / the amount above take effect (seeds
   * the employee's first `employee_compensation` row). `''` = let the backend
   * default it (employment start date, else today). Never loaded from an
   * existing record -- editing pay goes through the Change salary dialog.
   */
  compensation_effective_from: string;
  /** CREATE-only: earlier salary entries, all dated before the current one. */
  compensation_history: CompensationHistoryDraft[];
}

/** A blank draft for the create form -- mirrors the backend's own create-time defaults (`pay_basis: 'hourly'`, `hourly_rate_cents: 0`). */
export function emptyEmployeeFormDraft(): EmployeeFormDraft {
  return {
    full_name: '',
    email: '',
    pay_basis: 'hourly',
    pay_currency_code: '',
    monthly_salary_cents: 0,
    hourly_rate_cents: 0,
    employment_start_date: '',
    termination_date: '',
    is_active: true,
    employee_number: '',
    epf_member_no: '',
    nic_number: '',
    bank_name: '',
    bank_account_number: '',
    bank_branch: '',
    bank_code: '',
    npwp: '',
    nik: '',
    ptkp_status: '',
    bpjs_jkk_risk_class: null,
    job_title: '',
    department: '',
    work_location: '',
    employment_type: 'full_time',
    residency_status: 'resident',
    is_citizen: true,
    apit_employment_type: 'primary',
    apit_declared_primary_income_cents: null,
    apit_borne_by_employer: false,
    fte_percent: '',
    compensation_effective_from: '',
    compensation_history: [],
  };
}

/**
 * Validates a raw `ptkp_status` DB value against the known PTKP set --
 * mirrors `wagepro_store_employees.ts`'s `deriveEmploymentType` stance: the
 * column is free-text with no SQL CHECK constraint, so a legacy/corrupted
 * row could hold a value outside the closed set. Returns `''` (the form's
 * own "not set" option) rather than trusting or fabricating a value --
 * critically, this must NOT default to the backend's own `'TK/0'` column
 * default, or an untouched save of a legacy row with an actually-invalid
 * value would silently coerce it to `'TK/0'` instead of leaving it alone.
 */
function derivePtkpStatus(value: string | null): PtkpStatus | '' {
  return value !== null &&
    (PTKP_STATUS_OPTIONS as readonly string[]).includes(value)
    ? (value as PtkpStatus)
    : '';
}

/** Validates a raw `bpjs_jkk_risk_class` DB value against the accepted 1-5 range -- same "never trust the wire value" stance as {@link derivePtkpStatus}. */
function deriveBpjsRiskClass(value: number | null): number | null {
  return value !== null &&
    (BPJS_JKK_RISK_CLASSES as readonly number[]).includes(value)
    ? value
    : null;
}

/**
 * Converts a real `payroll.listEmployees` record into the edit form's draft
 * shape. `bank_account_number` is the one field the read path (
 * `wagepro_store_employees.ts`'s `toUiEmployee`) already masks for display
 * elsewhere -- this function reads the RAW record (never the masked
 * `Employee` UI type), so the edit form can show/resubmit the real number.
 * Every jurisdiction-specific ID field is loaded from the real record (not
 * left blank) -- see module docblock for why an earlier version leaving
 * these blank was a silent-data-destruction bug, not a cosmetic gap.
 */
export function employeeRecordToFormDraft(
  record: RealEmployeeRecord,
): EmployeeFormDraft {
  return {
    full_name: record.full_name,
    email: record.email ?? '',
    pay_basis: record.pay_basis,
    pay_currency_code: record.pay_currency_code ?? '',
    monthly_salary_cents: record.monthly_salary_cents ?? 0,
    hourly_rate_cents: record.hourly_rate_cents,
    employment_start_date: record.employment_start_date ?? '',
    termination_date: record.termination_date ?? '',
    is_active: record.is_active,
    employee_number: record.employee_number ?? '',
    epf_member_no: record.epf_member_no ?? '',
    nic_number: record.nic_number ?? '',
    bank_name: record.bank_name ?? '',
    bank_account_number: record.bank_account_number ?? '',
    bank_branch: record.bank_branch ?? '',
    bank_code: record.bank_code ?? '',
    npwp: record.npwp ?? '',
    nik: record.nik ?? '',
    ptkp_status: derivePtkpStatus(record.ptkp_status),
    bpjs_jkk_risk_class: deriveBpjsRiskClass(record.bpjs_jkk_risk_class),
    job_title: record.job_title ?? '',
    department: record.department ?? '',
    work_location: record.work_location ?? '',
    employment_type: deriveEmploymentType(record.employment_type),
    residency_status: deriveResidencyStatus(record.residency_status),
    is_citizen: record.is_citizen ?? true,
    apit_employment_type: deriveApitEmploymentType(record.apit_employment_type),
    apit_declared_primary_income_cents:
      record.apit_declared_primary_income_cents ?? null,
    apit_borne_by_employer: record.apit_borne_by_employer ?? false,
    fte_percent: fteBpsToPercentInput(record),
    compensation_effective_from: '',
    compensation_history: [],
  };
}

/**
 * A part-timer's stored FTE as the form's whole-percent text. Blank for a
 * non-part-time worker. A legacy part-timer with NO FTE on file (NULL,
 * which the API reads as 100%) is prefilled as `'100'` -- never blank, so
 * the required-FTE check cannot block an unrelated save of that row. A
 * non-whole percentage (e.g. 5050 bps written by the API directly) is shown
 * rounded half-up via `fte_bps_to_percent`; {@link buildUpdateEmployeeInput}
 * only sends `fte_bps` when the user edits this text, so the stored 5050 is
 * never rewritten to 5100 by a save that did not touch the field.
 */
export function fteBpsToPercentInput(record: RealEmployeeRecord): string {
  if (deriveEmploymentType(record.employment_type) !== 'part_time') return '';
  return String(fte_bps_to_percent(record.fte_bps) ?? 100);
}

/** Parses the FTE text into whole percent, or `null` when blank/not an integer. */
function parseFtePercent(value: string): number | null {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Number(trimmed);
}

/** The `fte_bps` the draft sends: basis points for a valid part-time FTE, else `null`. */
function draftFteBps(draft: EmployeeFormDraft): number | null {
  if (draft.employment_type !== 'part_time') return null;
  const percent = parseFtePercent(draft.fte_percent);
  return percent === null ? null : fte_percent_to_bps(percent);
}

/** Every field this form validates -- the key space of {@link EmployeeFormErrors}. */
export type EmployeeFormFieldKey =
  | 'full_name'
  | 'email'
  | 'monthly_salary_cents'
  | 'hourly_rate_cents'
  | 'termination_date'
  | 'employee_number'
  | 'nic_number'
  | 'epf_member_no'
  | 'npwp'
  | 'nik'
  | 'bpjs_jkk_risk_class'
  | 'apit_declared_primary_income_cents'
  | 'fte_percent'
  | 'compensation_effective_from'
  | 'compensation_history';

/**
 * Closed set of validation-failure reasons. A code, never a raw string --
 * the component maps each code to a `t(...)` key (CLAUDE.md's i18n
 * mandate: no hardcoded JSX strings, and a validation reason must be
 * translatable like everything else user-visible).
 */
export type EmployeeFormFieldError =
  | 'required'
  | 'too_long'
  | 'invalid_email'
  | 'salary_required'
  | 'negative_amount'
  | 'termination_before_start'
  | 'invalid_nic'
  | 'invalid_nic_day'
  | 'epf_empty'
  | 'epf_control_chars'
  | 'invalid_risk_class'
  | 'fte_required'
  | 'fte_out_of_range'
  | 'effective_from_before_start'
  | 'history_invalid';

/** Mirrors `employeeProfileInput`'s `z.string().max(N)` caps (`apps/api/src/trpc/routers/payroll.ts`) -- catches an over-length value client-side instead of bouncing off a raw BAD_REQUEST. */
const FULL_NAME_MAX_LENGTH = 200;
const NPWP_MAX_LENGTH = 20;
const NIK_MAX_LENGTH = 16;
const EMPLOYEE_NUMBER_MAX_LENGTH = 32;

export type EmployeeFormErrors = Partial<
  Record<EmployeeFormFieldKey, EmployeeFormFieldError>
>;

// ── Sri Lanka NIC / EPF format validation ───────────────────────────────────
// Delegates to the shared `@jumpstone/core` validators (`validateSlNic` /
// `validateSlEpfNumber`) — the same implementation the API and the migration
// importer use — so the three can no longer drift. This file only maps the
// core result onto the form's translatable error codes.

/** Validate a NIC via the shared core validator. `null` means valid. Only call for a non-empty value — an empty NIC is "not yet provided", not invalid. */
export function validateNicNumber(
  value: string,
): EmployeeFormFieldError | null {
  const result = validateSlNic(value);
  if (result.ok) return null;
  return result.reason.includes('day-of-year')
    ? 'invalid_nic_day'
    : 'invalid_nic';
}

/** Validate an EPF member number via the shared core validator. `null` means valid. Only call for a non-empty value — see {@link validateNicNumber}. */
export function validateEpfMemberNo(
  value: string,
): EmployeeFormFieldError | null {
  const result = validateSlEpfNumber(value);
  if (result.ok) return null;
  return result.reason.includes('empty') ? 'epf_empty' : 'epf_control_chars';
}

/** Loose email-shape check -- mirrors zod's `.email()` closely enough to catch the common "forgot the @" mistake before a round trip; the backend's own `z.string().email()` remains the authoritative check. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Effective date of the CURRENT pay entry as the backend will resolve it:
 * the explicit "Salary effective from", else the employment start date, else
 * today (`createEmployee`'s documented default chain).
 */
export function resolveCurrentEffectiveFrom(
  draft: Pick<
    EmployeeFormDraft,
    'compensation_effective_from' | 'employment_start_date'
  >,
): string {
  if (draft.compensation_effective_from !== '') {
    return draft.compensation_effective_from;
  }
  if (draft.employment_start_date !== '') return draft.employment_start_date;
  return todayIsoUtc();
}

/** Create-only checks for the "Salary effective from" date and the earlier-entries repeater. */
function validateCompensationDraft(
  draft: EmployeeFormDraft,
): Pick<
  EmployeeFormErrors,
  'compensation_effective_from' | 'compensation_history'
> {
  const errors: Pick<
    EmployeeFormErrors,
    'compensation_effective_from' | 'compensation_history'
  > = {};
  if (
    draft.compensation_effective_from !== '' &&
    draft.employment_start_date !== '' &&
    draft.compensation_effective_from < draft.employment_start_date
  ) {
    errors.compensation_effective_from = 'effective_from_before_start';
  }
  if (
    historyRowsHaveErrors(draft.compensation_history, {
      current_effective_from: resolveCurrentEffectiveFrom(draft),
      employment_start_date: draft.employment_start_date,
    })
  ) {
    errors.compensation_history = 'history_invalid';
  }
  return errors;
}

/**
 * Validates the full draft against every rule the backend enforces for
 * `createEmployee`/`updateEmployee`, gated by `jurisdiction` for the two
 * jurisdiction-specific ID groups (module docblock). Returns an empty object
 * when the draft is fully valid.
 */
export function validateEmployeeForm(
  draft: EmployeeFormDraft,
  jurisdiction: EmployeeJurisdiction,
  mode: 'create' | 'edit' = 'create',
): EmployeeFormErrors {
  const errors: EmployeeFormErrors = {};

  if (draft.full_name.trim().length === 0) {
    errors.full_name = 'required';
  } else if (draft.full_name.trim().length > FULL_NAME_MAX_LENGTH) {
    errors.full_name = 'too_long';
  }

  if (
    draft.email.trim().length > 0 &&
    !EMAIL_PATTERN.test(draft.email.trim())
  ) {
    errors.email = 'invalid_email';
  }

  // Mirrors `validateWorkerTypeFields`: a part-timer needs a whole-percent
  // FTE of 1-100 (the backend accepts 1-10000 bps; this form only offers
  // whole percents). Worker type is editable in both modes.
  if (draft.employment_type === 'part_time') {
    const trimmed = draft.fte_percent.trim();
    if (trimmed.length === 0) {
      errors.fte_percent = 'fte_required';
    } else {
      const percent = parseFtePercent(trimmed);
      if (percent === null || fte_percent_to_bps(percent) === null) {
        errors.fte_percent = 'fte_out_of_range';
      }
    }
  }

  // Pay (basis / amount / currency) is only editable on CREATE. In edit mode
  // the form shows it read-only and changes go through the Change salary
  // dialog (`change_salary_validation.ts`), so an untouched -- possibly
  // legitimately unconfigured, e.g. a salaried joiner with no salary yet --
  // pay value must never block an unrelated edit behind an invisible error.
  if (mode === 'create') {
    // Mirrors `validateEmployeeProfile`'s unconditional negative-salary check.
    if (draft.monthly_salary_cents < 0) {
      errors.monthly_salary_cents = 'negative_amount';
    } else if (
      draft.pay_basis === 'salaried' &&
      draft.employment_type !== 'contractor' &&
      draft.monthly_salary_cents <= 0
    ) {
      // Mirrors "a salaried employee requires a positive monthly_salary_cents"
      // -- skipped for a contractor, who is never paid through a pay run.
      errors.monthly_salary_cents = 'salary_required';
    }

    if (draft.hourly_rate_cents < 0) {
      errors.hourly_rate_cents = 'negative_amount';
    }

    const compensationErrors = validateCompensationDraft(draft);
    if (compensationErrors.compensation_effective_from !== undefined) {
      errors.compensation_effective_from =
        compensationErrors.compensation_effective_from;
    }
    if (compensationErrors.compensation_history !== undefined) {
      errors.compensation_history = compensationErrors.compensation_history;
    }
  }

  // Mirrors `employee_number: z.string().trim().max(32)` (V395).
  if (draft.employee_number.trim().length > EMPLOYEE_NUMBER_MAX_LENGTH) {
    errors.employee_number = 'too_long';
  }

  // Mirrors "termination_date must be on or after employment_start_date".
  if (
    draft.employment_start_date.length > 0 &&
    draft.termination_date.length > 0 &&
    draft.termination_date < draft.employment_start_date
  ) {
    errors.termination_date = 'termination_before_start';
  }

  if (jurisdiction === 'LK') {
    const trimmedNic = draft.nic_number.trim();
    if (trimmedNic.length > 0) {
      const nicError = validateNicNumber(trimmedNic);
      if (nicError !== null) errors.nic_number = nicError;
    }
    const trimmedEpf = draft.epf_member_no.trim();
    // The EPF field is hidden for a contractor (not an EPF member), so a
    // hidden legacy value must not block the save.
    if (trimmedEpf.length > 0 && draft.employment_type !== 'contractor') {
      const epfError = validateEpfMemberNo(trimmedEpf);
      if (epfError !== null) errors.epf_member_no = epfError;
    }
    // ADR-168: the tax-status group is hidden for a contractor, so a hidden
    // value must not block the save.
    if (
      draft.employment_type !== 'contractor' &&
      draft.apit_declared_primary_income_cents !== null &&
      draft.apit_declared_primary_income_cents < 0
    ) {
      errors.apit_declared_primary_income_cents = 'negative_amount';
    }
  }

  if (jurisdiction === 'ID') {
    if (draft.npwp.trim().length > NPWP_MAX_LENGTH) {
      errors.npwp = 'too_long';
    }
    if (draft.nik.trim().length > NIK_MAX_LENGTH) {
      errors.nik = 'too_long';
    }
    if (
      draft.employment_type !== 'contractor' &&
      draft.bpjs_jkk_risk_class !== null &&
      !(BPJS_JKK_RISK_CLASSES as readonly number[]).includes(
        draft.bpjs_jkk_risk_class,
      )
    ) {
      errors.bpjs_jkk_risk_class = 'invalid_risk_class';
    }
  }

  return errors;
}

function nullIfBlank(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** The `payroll.createEmployee` wire input this form builds (minus `firm_id`/`entity_id`, attached by the mutation hook, mirroring `use_pay_runs.ts`'s "pure delta builder, ids attached by the executor" convention). Every optional field is `null` (never `''`) when unset -- there is no prior record to diff against on create, so every optional field genuinely starts unset. No `preferred_locale` -- see module docblock. */
export interface CreateEmployeeInput {
  full_name: string;
  email: string | undefined;
  hourly_rate_cents: number;
  pay_basis: PayBasis;
  /** `null` = organisation base currency. */
  pay_currency_code: string | null;
  monthly_salary_cents: number | null;
  employment_start_date: string | null;
  termination_date: string | null;
  employee_number: string | null;
  epf_member_no: string | null;
  nic_number: string | null;
  bank_name: string | null;
  bank_account_number: string | null;
  bank_branch: string | null;
  bank_code: string | null;
  npwp: string | null;
  nik: string | null;
  ptkp_status: PtkpStatus | null;
  bpjs_jkk_risk_class: number | null;
  job_title: string | null;
  department: string | null;
  work_location: string | null;
  employment_type: EmploymentType;
  fte_bps: number | null;
  /** SL tax facts (#26653 / ADR-168); defaults for a contractor, who is not on payroll. */
  residency_status: TaxResidency;
  is_citizen: boolean;
  apit_employment_type: ApitEmploymentType;
  /** `null` unless secondary with an amount > 0 (a typed 0 is "none declared": the form cannot express a declared Rs 0). */
  apit_declared_primary_income_cents: number | null;
  /** Employer bears the income tax (IRD Table 06); always `false` where unavailable. */
  apit_borne_by_employer: boolean;
  /** Omitted when blank -- the backend defaults it (employment start date, else today). */
  compensation_effective_from?: string;
  /** Omitted when there are no earlier entries. */
  compensation_history?: CompensationHistoryInput[];
}

export function buildCreateEmployeeInput(
  draft: EmployeeFormDraft,
): CreateEmployeeInput {
  // A contractor is not an EPF member / PTKP / BPJS participant: those
  // fields are hidden on the form, so a value typed before switching the
  // type to contractor is never submitted.
  const contractor = draft.employment_type === 'contractor';
  return {
    full_name: draft.full_name.trim(),
    email: nullIfBlank(draft.email) ?? undefined,
    hourly_rate_cents: draft.hourly_rate_cents,
    pay_basis: draft.pay_basis,
    pay_currency_code: nullIfBlank(draft.pay_currency_code),
    monthly_salary_cents:
      draft.pay_basis === 'salaried' ? draft.monthly_salary_cents : null,
    employment_start_date: nullIfBlank(draft.employment_start_date),
    termination_date: nullIfBlank(draft.termination_date),
    employee_number: nullIfBlank(draft.employee_number),
    epf_member_no: contractor ? null : nullIfBlank(draft.epf_member_no),
    nic_number: nullIfBlank(draft.nic_number),
    bank_name: nullIfBlank(draft.bank_name),
    bank_account_number: nullIfBlank(draft.bank_account_number),
    bank_branch: nullIfBlank(draft.bank_branch),
    bank_code: nullIfBlank(draft.bank_code),
    npwp: nullIfBlank(draft.npwp),
    nik: nullIfBlank(draft.nik),
    ptkp_status:
      contractor || draft.ptkp_status === '' ? null : draft.ptkp_status,
    bpjs_jkk_risk_class: contractor ? null : draft.bpjs_jkk_risk_class,
    job_title: nullIfBlank(draft.job_title),
    department: nullIfBlank(draft.department),
    work_location: nullIfBlank(draft.work_location),
    employment_type: draft.employment_type,
    fte_bps: draftFteBps(draft),
    ...buildLkTaxCreateFields(draft, contractor),
    compensation_effective_from:
      nullIfBlank(draft.compensation_effective_from) ?? undefined,
    compensation_history:
      draft.compensation_history.length > 0
        ? buildCompensationHistoryInput(
            draft.compensation_history,
            draft.pay_basis,
          )
        : undefined,
  };
}

/**
 * The `payroll.updateEmployee` wire input this form builds (minus
 * `firm_id`/`entity_id`/`employee_id`). `full_name`/`hourly_rate_cents`/
 * `is_active`/`pay_basis` are always sent (each is NOT NULL with a real
 * current value in the draft, so resending an unchanged value is a harmless
 * no-op, never destructive). Every other field is `undefined` unless it
 * actually changed from the loaded record -- see module docblock's
 * "Update semantics" section for why this is load-bearing, not a style
 * choice: a `null` sent for an unchanged field WOULD destroy real EPF/NIC/
 * bank/statutory data server-side.
 */
export interface UpdateEmployeeInput {
  full_name: string;
  hourly_rate_cents: number;
  is_active: boolean;
  pay_basis: PayBasis;
  /** `null` = reset to base currency; `undefined` = unchanged. */
  pay_currency_code?: string | null;
  email?: string | null;
  monthly_salary_cents?: number | null;
  employment_start_date?: string | null;
  termination_date?: string | null;
  employee_number?: string | null;
  epf_member_no?: string | null;
  nic_number?: string | null;
  bank_name?: string | null;
  bank_account_number?: string | null;
  bank_branch?: string | null;
  bank_code?: string | null;
  npwp?: string | null;
  nik?: string | null;
  ptkp_status?: PtkpStatus | null;
  bpjs_jkk_risk_class?: number | null;
  job_title?: string | null;
  department?: string | null;
  work_location?: string | null;
  employment_type?: EmploymentType;
  fte_bps?: number | null;
  residency_status?: TaxResidency;
  is_citizen?: boolean;
  apit_employment_type?: ApitEmploymentType;
  /** `null` clears; `undefined` = unchanged. */
  apit_declared_primary_income_cents?: number | null;
  apit_borne_by_employer?: boolean;
}

/** The declared primary income the draft sends: only for a secondary employee, and only when > 0. */
function draftDeclaredPrimaryIncome(draft: EmployeeFormDraft): number | null {
  return draft.apit_employment_type === 'secondary' &&
    draft.apit_declared_primary_income_cents !== null &&
    draft.apit_declared_primary_income_cents > 0
    ? draft.apit_declared_primary_income_cents
    : null;
}

/**
 * Employer-borne income tax (IRD Table 06) has no table for a non-resident
 * non-citizen (Table 04) or a resident secondary employment (Table 07); the
 * API refuses both, so the form disables the switch and never sends it.
 */
// Mirrors `isApitBorneUnsupported` in @ledger/core (`sl_payroll.ts`, ADR-170),
// the canonical rule; wagepro-web has no core dependency, so keep in sync.
export function isApitBorneUnavailable(
  draft: Pick<
    EmployeeFormDraft,
    'residency_status' | 'is_citizen' | 'apit_employment_type'
  >,
): boolean {
  if (draft.apit_employment_type === 'secondary') {
    return draft.residency_status === 'resident';
  }
  return draft.residency_status === 'non_resident' && !draft.is_citizen;
}

/** The employer-borne flag the draft sends: `false` wherever it is unavailable. */
function draftApitBorne(draft: EmployeeFormDraft): boolean {
  return draft.apit_borne_by_employer && !isApitBorneUnavailable(draft);
}

/** CREATE: the SL tax facts. A contractor (group hidden, not on payroll) sends the backend defaults. */
function buildLkTaxCreateFields(
  draft: EmployeeFormDraft,
  contractor: boolean,
): Pick<
  CreateEmployeeInput,
  | 'residency_status'
  | 'is_citizen'
  | 'apit_employment_type'
  | 'apit_declared_primary_income_cents'
  | 'apit_borne_by_employer'
> {
  if (contractor) {
    return {
      residency_status: 'resident',
      is_citizen: true,
      apit_employment_type: 'primary',
      apit_declared_primary_income_cents: null,
      apit_borne_by_employer: false,
    };
  }
  return {
    residency_status: draft.residency_status,
    is_citizen: draft.is_citizen,
    apit_employment_type: draft.apit_employment_type,
    apit_declared_primary_income_cents: draftDeclaredPrimaryIncome(draft),
    apit_borne_by_employer: draftApitBorne(draft),
  };
}

/**
 * UPDATE: each SL tax fact is diffed against the loaded record (compared as
 * the RESOLVED stored value, so an untouched save of a legacy row sends
 * nothing). A contractor's hidden group is never submitted. The declared
 * amount is sent only when it changes; switching to primary clears a stored
 * amount (`null`), mirroring the API's own normalisation.
 */
function buildLkTaxUpdateFields(
  draft: EmployeeFormDraft,
  original: RealEmployeeRecord,
  contractor: boolean,
): Pick<
  UpdateEmployeeInput,
  | 'residency_status'
  | 'is_citizen'
  | 'apit_employment_type'
  | 'apit_declared_primary_income_cents'
  | 'apit_borne_by_employer'
> {
  if (contractor) return {};
  return {
    residency_status: diffOrUndefined(
      draft.residency_status,
      deriveResidencyStatus(original.residency_status),
    ),
    is_citizen: diffOrUndefined(draft.is_citizen, original.is_citizen ?? true),
    apit_employment_type: diffOrUndefined(
      draft.apit_employment_type,
      deriveApitEmploymentType(original.apit_employment_type),
    ),
    apit_declared_primary_income_cents: diffOrUndefined(
      draftDeclaredPrimaryIncome(draft),
      original.apit_declared_primary_income_cents ?? null,
    ),
    apit_borne_by_employer: diffOrUndefined(
      draftApitBorne(draft),
      original.apit_borne_by_employer ?? false,
    ),
  };
}

/**
 * `undefined` when `candidate` equals `original` (no change to send),
 * otherwise `candidate` itself -- NEVER `original`. `original` is
 * deliberately typed `unknown` (not the same `T` as `candidate`): the raw
 * `RealEmployeeRecord` field being diffed against can be a wider/less-
 * validated type than the form's own candidate value (e.g. `ptkp_status`'s
 * raw DB column is unconstrained `string | null`, while `candidate` is the
 * validated `PtkpStatus | null` this form actually produces) -- comparison
 * still works correctly via `===` regardless, and the return type is driven
 * solely by `candidate`, which is always what gets sent.
 */
function diffOrUndefined<T>(candidate: T, original: unknown): T | undefined {
  return (candidate as unknown) === original ? undefined : candidate;
}

/**
 * The update payload's `fte_bps`: `undefined` (no change) unless the FTE
 * actually changed. A part-timer's FTE is only sent when the displayed
 * percent text was edited (or the worker just became part-time); leaving
 * part-time sends `null` only when an FTE is on file (the API also clears
 * it automatically).
 */
function fteBpsUpdate(
  draft: EmployeeFormDraft,
  original: RealEmployeeRecord,
): number | null | undefined {
  if (draft.employment_type !== 'part_time') {
    return original.fte_bps === null ? undefined : null;
  }
  const wasPartTime =
    deriveEmploymentType(original.employment_type) === 'part_time';
  if (
    wasPartTime &&
    draft.fte_percent.trim() === fteBpsToPercentInput(original)
  ) {
    return undefined;
  }
  return diffOrUndefined(draftFteBps(draft), original.fte_bps);
}

/**
 * Builds the update payload as a DIFF against `original` (the loaded
 * `RealEmployeeRecord`) -- see module docblock. `original.ptkp_status` is
 * compared as the raw DB string (not run back through `derivePtkpStatus`):
 * if the stored value was already outside the known set, an untouched save
 * will still detect "candidate ('') !== original (the invalid string)" and
 * send `null` -- clearing an already-invalid value rather than leaving a
 * silently-corrupted field alone. Same reasoning applies to a stray
 * non-null `monthly_salary_cents` on an `hourly` original record: the
 * candidate is `null` (this form never shows a salary field for `hourly`),
 * which differs from the stale value and clears it -- a legitimate cleanup
 * of state the backend's own validation never should have allowed to
 * persist, not silent destruction of a value this form actively manages.
 */
export function buildUpdateEmployeeInput(
  draft: EmployeeFormDraft,
  original: RealEmployeeRecord,
): UpdateEmployeeInput {
  // A salaried employee with NO salary on record (null -- e.g. a joiner whose
  // pay was never configured) loads into the draft as 0. Diffing 0 against
  // null would resend `monthly_salary_cents: 0` on an unrelated edit, which
  // the backend rejects ("a salaried employee requires a positive monthly
  // salary") -- and pay is no longer editable here, so the user could not
  // fix it. Treat "0 in the draft, null on the record" as unchanged.
  const salaryUntouched =
    draft.pay_basis === 'salaried' &&
    draft.monthly_salary_cents === (original.monthly_salary_cents ?? 0);
  let monthlySalaryCandidate: number | null = null;
  if (draft.pay_basis === 'salaried') {
    monthlySalaryCandidate = salaryUntouched
      ? original.monthly_salary_cents
      : draft.monthly_salary_cents;
  }
  const ptkpCandidate = draft.ptkp_status === '' ? null : draft.ptkp_status;
  // A contractor's EPF / PTKP / BPJS fields are hidden on the form: never
  // submit what the draft still holds for them, and clear a value on file
  // (null) only when one exists -- otherwise send nothing.
  const contractor = draft.employment_type === 'contractor';
  const clearIfSet = (value: unknown): null | undefined =>
    value === null ? undefined : null;

  return {
    full_name: draft.full_name.trim(),
    hourly_rate_cents: draft.hourly_rate_cents,
    is_active: draft.is_active,
    pay_basis: draft.pay_basis,
    pay_currency_code: diffOrUndefined(
      nullIfBlank(draft.pay_currency_code),
      original.pay_currency_code,
    ),
    email: diffOrUndefined(nullIfBlank(draft.email), original.email),
    monthly_salary_cents: diffOrUndefined(
      monthlySalaryCandidate,
      original.monthly_salary_cents,
    ),
    employment_start_date: diffOrUndefined(
      nullIfBlank(draft.employment_start_date),
      original.employment_start_date,
    ),
    termination_date: diffOrUndefined(
      nullIfBlank(draft.termination_date),
      original.termination_date,
    ),
    employee_number: diffOrUndefined(
      nullIfBlank(draft.employee_number),
      original.employee_number,
    ),
    epf_member_no: contractor
      ? clearIfSet(original.epf_member_no)
      : diffOrUndefined(
          nullIfBlank(draft.epf_member_no),
          original.epf_member_no,
        ),
    nic_number: diffOrUndefined(
      nullIfBlank(draft.nic_number),
      original.nic_number,
    ),
    bank_name: diffOrUndefined(
      nullIfBlank(draft.bank_name),
      original.bank_name,
    ),
    bank_account_number: diffOrUndefined(
      nullIfBlank(draft.bank_account_number),
      original.bank_account_number,
    ),
    bank_branch: diffOrUndefined(
      nullIfBlank(draft.bank_branch),
      original.bank_branch,
    ),
    bank_code: diffOrUndefined(
      nullIfBlank(draft.bank_code),
      original.bank_code,
    ),
    npwp: diffOrUndefined(nullIfBlank(draft.npwp), original.npwp),
    nik: diffOrUndefined(nullIfBlank(draft.nik), original.nik),
    ptkp_status: contractor
      ? clearIfSet(original.ptkp_status)
      : diffOrUndefined(ptkpCandidate, original.ptkp_status),
    bpjs_jkk_risk_class: contractor
      ? clearIfSet(original.bpjs_jkk_risk_class)
      : diffOrUndefined(
          draft.bpjs_jkk_risk_class,
          original.bpjs_jkk_risk_class,
        ),
    job_title: diffOrUndefined(
      nullIfBlank(draft.job_title),
      original.job_title,
    ),
    department: diffOrUndefined(
      nullIfBlank(draft.department),
      original.department,
    ),
    work_location: diffOrUndefined(
      nullIfBlank(draft.work_location),
      original.work_location,
    ),
    // Compared against the RESOLVED stored type, so an untouched save of a
    // legacy NULL row (read as full-time) sends nothing.
    employment_type: diffOrUndefined(
      draft.employment_type,
      deriveEmploymentType(original.employment_type),
    ),
    // Sent only when the FTE actually changed -- see fteBpsUpdate.
    fte_bps: fteBpsUpdate(draft, original),
    ...buildLkTaxUpdateFields(draft, original, contractor),
  };
}
